import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';
import { CommonModule, KeyValue } from '@angular/common';
import { CheckboxComponent } from '../../base/checkbox/checkbox.component';
import { CheckboxNode, flattenNodeMap } from './checkboxes.models';

@Component({
  selector: 'jmkw-checkboxes',
  templateUrl: './checkboxes.component.html',
  standalone: true,
  imports: [CommonModule, CheckboxComponent],
  styleUrls: ['./checkboxes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxesComponent {
  @Input() expandable = true;
  @Input() checkboxNodeMap!: ExtendedMap<CheckboxNode>;
  @Input() automationKey?: string;

  @Output() nodeChanged = new EventEmitter<KeyValue<string, CheckboxNode>>();
  @Output() hierarchyChange = new EventEmitter<ExtendedMap<CheckboxNode>>();
  @Output() flatChange = new EventEmitter<CheckboxNode[]>();

  trackByNode(_: number, item: KeyValue<string, CheckboxNode>): string {
    const childrenKeys = Array.from(item.value.children.keys()).join(',');
    return `${item.key}-${item.value.selected}-${item.value.indeterminate}-${childrenKeys}`;
  }

  checkboxChangeHandler(
    selected: boolean,
    checkboxKeyValue: KeyValue<string, CheckboxNode>
  ): void {
    const node = checkboxKeyValue.value;
    const shouldDeselect = node.indeterminate;
    const updatedNode = this.toggle(node, shouldDeselect ? false : selected);
    this.checkboxNodeMap.set(checkboxKeyValue.key, updatedNode);
    this.emitChanges(checkboxKeyValue.key, updatedNode);
  }

  toggle(node: CheckboxNode, selected: boolean): CheckboxNode {
    node.selected = selected;
    node.indeterminate = false;
    node.onChange?.(selected, node);
    node.children.forEach((child, key) => {
      node.children.set(key, this.toggle(child, selected));
    });
    return new CheckboxNode(node);
  }

  onChildChange(
    changedKeyValue: KeyValue<string, CheckboxNode>,
    parentKey: string
  ): void {
    const parentNode = this.checkboxNodeMap.get(parentKey);
    if (parentNode) {
      if (parentNode.children.has(changedKeyValue.key)) {
        parentNode.children.set(changedKeyValue.key, changedKeyValue.value);
      }
      parentNode.selected = this._allSelected(parentNode);
      parentNode.indeterminate = this._someSelected(parentNode);
      this.checkboxNodeMap.set(parentKey, new CheckboxNode(parentNode));

      // Emit the original changed node
      this.nodeChanged.emit(changedKeyValue);

      // Emit the hierarchy and flat representations
      this.hierarchyChange.emit(this.checkboxNodeMap);
      this.flatChange.emit(flattenNodeMap(this.checkboxNodeMap));
    }
  }

  private emitChanges(key: string, node: CheckboxNode): void {
    this.nodeChanged.emit({ key, value: node });
    this.hierarchyChange.emit(this.checkboxNodeMap);
    this.flatChange.emit(flattenNodeMap(this.checkboxNodeMap));
  }

  toggleExpandHandler(e: Event, key: string): void {
    e.preventDefault();
    const node = this.checkboxNodeMap.get(key);
    if (node) {
      node.expanded = !node.expanded;
      this.emitChanges(key, new CheckboxNode(node));
    }
  }

  private _allSelected(node: CheckboxNode): boolean {
    return [...node.children.values()].every((child) => child.selected);
  }

  private _someSelected(node: CheckboxNode): boolean {
    return (
      !node.selected &&
      [...node.children.values()].some(
        (child) => child.selected || child.indeterminate
      )
    );
  }
}
